/**
 * Walk 3 — which tenant the portal is reading, for a principal who may read more than one.
 *
 * <p>A CLIENT principal is provisioned with exactly one tenant, and for them nothing here changes:
 * the tenant is `allowed_tenant_ids[0]`, there is no switcher, and no `?tenant=` is ever read or
 * written. An OPERATOR or platform admin signed in to the portal (the go-live walk's case) has
 * several, and before this module the portal silently showed them the first — which is how an
 * admin opened the Onboarding board and got another firm's (non-)answer.</p>
 *
 * <p>This file is the PURE half: the rules for which tenants are offered, how a requested token
 * resolves, and the browser-storage convenience. The React half (the provider that keeps the choice
 * in the URL and the switcher itself) is `nav/TenantSelection.tsx`, which is where the router and
 * the query cache are. The split keeps `portalQueries.ts` free of a router dependency: its hooks
 * read the choice through {@link TenantSelectionContext} and fall back to the first tenant when no
 * provider is mounted, exactly as they behaved before.</p>
 *
 * <h3>Rules</h3>
 * <ul>
 *   <li><b>No invented tenants.</b> The options are the principal's own `allowed_tenant_ids`, in
 *       that order, that `/me` also named in `allowed_tenants`. A platform admin's `/me` lists
 *       EVERY tenant in `allowed_tenants`; they are offered their allowed set, not the whole
 *       platform — UNLESS their allowed set is empty (an admin row needs no allowed ids: it reads
 *       every tenant). Then the options are `allowed_tenants` itself, the tenants `/me` says they
 *       can read, in `/me`'s order (6 Oct 2026, prod: such an admin was told "Your account is not
 *       set up yet").</li>
 *   <li><b>The default does not move.</b> Nothing requested, or a request for a tenant the
 *       principal cannot read, resolves to `allowed_tenant_ids[0]` (or, with no allowed ids, the
 *       first option) — never to "no tenant" while there is one to read, and never to the
 *       requested value on trust.</li>
 *   <li><b>A token is a slug or an id.</b> The URL carries the slug because a link is read by
 *       people; an id is accepted too, so a link built from the wire still works.</li>
 * </ul>
 */
import { createContext } from "react";
import type { MeResponse, TenantSummary } from "./types.js";

/** The query parameter the choice lives in — shareable, bookmarkable, back-button friendly. */
export const TENANT_PARAM = "tenant";

/** The localStorage key for the remembered choice (a per-browser convenience, never the truth). */
export const TENANT_STORAGE_KEY = "ardept.portal.tenant";

/**
 * What a mounted provider hands the hooks below it: the token the viewer asked for, and a way to
 * ask for another. `null` (no provider) means "the first tenant", the pre-switcher behaviour.
 */
export interface TenantSelection {
  requested: string | null;
  choose: (tenant: TenantSummary) => void;
}

export const TenantSelectionContext = createContext<TenantSelection | null>(null);

/**
 * The tenants this principal can be switched between, in `allowed_tenant_ids` order (so the
 * default is always first). An id `/me` gave no summary for is left out — the switcher has no
 * name to show for it, and a GUID is not a label — but it can still be the default.
 */
export function tenantOptions(me: MeResponse | undefined): TenantSummary[] {
  if (!me) return [];
  // A platform admin with no allowed ids reads every tenant: /me lists them all in allowed_tenants,
  // so those ARE the tenants they can choose. Never applied to a non-admin.
  if (me.is_platform_admin && me.allowed_tenant_ids.length === 0) {
    const all: TenantSummary[] = [];
    for (const tenant of me.allowed_tenants) {
      if (!all.some((o) => o.id === tenant.id)) all.push(tenant);
    }
    return all;
  }
  const byId = new Map(me.allowed_tenants.map((tenant) => [tenant.id, tenant]));
  const options: TenantSummary[] = [];
  for (const id of me.allowed_tenant_ids) {
    const summary = byId.get(id);
    if (summary && !options.some((o) => o.id === id)) options.push(summary);
  }
  return options;
}

/** True when this principal gets a switcher at all. A client never does. */
export function canSwitchTenant(me: MeResponse | undefined): boolean {
  return tenantOptions(me).length > 1;
}

/**
 * The tenant a request resolves to. Single-tenant principals ignore the request entirely (so a
 * pasted `?tenant=` cannot change what a client sees); everyone else gets the requested tenant if
 * it is one of their options, else their default.
 */
export function resolveTenantId(me: MeResponse | undefined, requested: string | null): string | null {
  if (!me) return null;
  const fallback = me.allowed_tenant_ids[0] ?? tenantOptions(me)[0]?.id ?? null;
  if (requested === null || !canSwitchTenant(me)) return fallback;
  const match = tenantOptions(me).find((t) => t.id === requested || t.slug === requested);
  return match?.id ?? fallback;
}

/** The `/me` summary for a tenant id, looked up BY ID (allowed_tenants is ordered by slug). */
export function tenantSummary(
  me: MeResponse | undefined,
  tenantId: string | null,
): TenantSummary | null {
  if (!me || tenantId === null) return null;
  return me.allowed_tenants.find((t) => t.id === tenantId) ?? null;
}

/** The remembered token, or null — including whenever storage is unavailable or throws. */
export function readRememberedTenant(): string | null {
  try {
    return window.localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Remember a token for next time. Silent when storage is unavailable: it is a convenience. */
export function rememberTenant(token: string): void {
  try {
    window.localStorage.setItem(TENANT_STORAGE_KEY, token);
  } catch {
    // Private window, blocked site data, a quota — the URL still carries the choice.
  }
}

/** `search` with the tenant parameter set to `token`, every other parameter preserved. */
export function withTenantParam(search: string, token: string): string {
  const params = new URLSearchParams(search);
  params.set(TENANT_PARAM, token);
  return `?${params.toString()}`;
}
